import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const StatusUpdateControls = ({ asset, isEditing, onUpdate }) => {
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    newStatus: asset?.status || 'active',
    reason: '',
    notes: '',
    scheduledDate: '',
    notifyOwner: true
  });

  const statusOptions = [
    { 
      value: 'active', 
      label: 'Active', 
      description: 'Asset is in use and operational',
      color: 'bg-success text-success-foreground',
      icon: 'CheckCircle'
    },
    { 
      value: 'spare', 
      label: 'Spare', 
      description: 'Asset is available for deployment',
      color: 'bg-warning text-warning-foreground',
      icon: 'Package'
    },
    { 
      value: 'repair', 
      label: 'Under Repair', 
      description: 'Asset requires maintenance or repair',
      color: 'bg-error text-error-foreground',
      icon: 'Wrench'
    },
    { 
      value: 'retired', 
      label: 'Retired', 
      description: 'Asset is no longer in service',
      color: 'bg-secondary text-secondary-foreground',
      icon: 'Archive'
    }
  ];

  const reasonOptions = [
    { value: 'deployment', label: 'New Deployment' },
    { value: 'maintenance', label: 'Scheduled Maintenance' },
    { value: 'repair', label: 'Repair Required' },
    { value: 'upgrade', label: 'Hardware Upgrade' },
    { value: 'replacement', label: 'Replacement' },
    { value: 'end-of-life', label: 'End of Life' },
    { value: 'policy', label: 'Policy Change' },
    { value: 'other', label: 'Other' }
  ];

  const statusHistory = asset?.statusHistory || [
    {
      id: 1,
      status: 'active',
      previousStatus: 'spare',
      changedBy: 'John Smith',
      changedDate: '2025-08-15T10:30:00Z',
      reason: 'deployment',
      notes: 'Deployed to IT office for daily use',
      isCurrent: true
    },
    {
      id: 2,
      status: 'spare',
      previousStatus: 'repair',
      changedBy: 'Tech Support',
      changedDate: '2025-08-10T14:15:00Z',
      reason: 'repair',
      notes: 'Repair completed, ready for deployment',
      isCurrent: false
    },
    {
      id: 3,
      status: 'repair',
      previousStatus: 'active',
      changedBy: 'Sarah Johnson',
      changedDate: '2025-08-05T09:00:00Z',
      reason: 'maintenance',
      notes: 'Hardware issue reported, sent for repair',
      isCurrent: false
    }
  ];

  const getCurrentStatusInfo = () => {
    return statusOptions?.find(option => option?.value === asset?.status) || statusOptions?.[0];
  };

  const handleStatusUpdate = () => {
    if (statusUpdate?.newStatus && statusUpdate?.reason) {
      const newStatusRecord = {
        id: Date.now(),
        status: statusUpdate?.newStatus,
        previousStatus: asset?.status,
        changedBy: 'Current User',
        changedDate: new Date()?.toISOString(),
        reason: statusUpdate?.reason,
        notes: statusUpdate?.notes,
        scheduledDate: statusUpdate?.scheduledDate,
        isCurrent: true
      };

      // Update status history
      const updatedHistory = statusHistory?.map(record => 
        record?.isCurrent ? { ...record, isCurrent: false } : record
      );
      updatedHistory?.push(newStatusRecord);

      // Update asset
      const updatedAsset = {
        ...asset,
        status: statusUpdate?.newStatus,
        statusHistory: updatedHistory,
        lastUpdated: new Date()?.toISOString()
      };

      onUpdate(updatedAsset);

      // Reset form
      setStatusUpdate({
        newStatus: statusUpdate?.newStatus,
        reason: '',
        notes: '',
        scheduledDate: '',
        notifyOwner: true
      });
      setShowStatusChange(false);

      // Simulate notification
      if (statusUpdate?.notifyOwner) {
        console.log('Status change notification sent to asset owner');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentStatusInfo = getCurrentStatusInfo();

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Status Management</h3>
        {isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStatusChange(!showStatusChange)}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Update Status
          </Button>
        )}
      </div>
      {/* Current Status Display */}
      <div className="bg-muted rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${currentStatusInfo?.color}`}>
              <Icon name={currentStatusInfo?.icon} size={20} color="white" />
            </div>
            <div>
              <h4 className="text-lg font-medium text-foreground">{currentStatusInfo?.label}</h4>
              <p className="text-sm text-muted-foreground">{currentStatusInfo?.description}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Last Updated</p>
            <p className="font-medium text-foreground">
              {asset?.lastUpdated ? formatDate(asset?.lastUpdated) : 'Unknown'}
            </p>
          </div>
        </div>
      </div>
      {/* Quick Status Actions */}
      {!showStatusChange && isEditing && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {statusOptions?.map((status) => (
            <Button
              key={status?.value}
              variant={asset?.status === status?.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setStatusUpdate({ ...statusUpdate, newStatus: status?.value });
                setShowStatusChange(true);
              }}
              disabled={asset?.status === status?.value}
              className="flex flex-col items-center p-3 h-auto"
            >
              <Icon name={status?.icon} size={16} className="mb-1" />
              <span className="text-xs">{status?.label}</span>
            </Button>
          ))}
        </div>
      )}
      {/* Status Change Form */}
      {showStatusChange && isEditing && (
        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-6">
          <h4 className="text-md font-medium text-foreground mb-4">Update Asset Status</h4>
          
          <div className="space-y-4">
            <Select
              label="New Status"
              options={statusOptions?.map(opt => ({ 
                value: opt?.value, 
                label: opt?.label,
                description: opt?.description 
              }))}
              value={statusUpdate?.newStatus}
              onChange={(value) => setStatusUpdate({ ...statusUpdate, newStatus: value })}
              required
            />

            <Select
              label="Reason for Change"
              options={reasonOptions}
              value={statusUpdate?.reason}
              onChange={(value) => setStatusUpdate({ ...statusUpdate, reason: value })}
              required
              placeholder="Select reason for status change"
            />

            <Input
              label="Scheduled Date (Optional)"
              type="datetime-local"
              value={statusUpdate?.scheduledDate}
              onChange={(e) => setStatusUpdate({ ...statusUpdate, scheduledDate: e?.target?.value })}
              description="When should this status change take effect?"
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Notes
              </label>
              <textarea
                value={statusUpdate?.notes}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e?.target?.value })}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Add any additional notes about this status change..."
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="notifyOwner"
                checked={statusUpdate?.notifyOwner}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, notifyOwner: e?.target?.checked })}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring"
              />
              <label htmlFor="notifyOwner" className="text-sm text-foreground">
                Notify asset owner of status change
              </label>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="ghost" onClick={() => setShowStatusChange(false)}>
                Cancel
              </Button>
              <Button 
                variant="default" 
                onClick={handleStatusUpdate}
                disabled={!statusUpdate?.newStatus || !statusUpdate?.reason}
                iconName="Save"
                iconPosition="left"
              >
                Update Status
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Status History */}
      <div>
        <h4 className="text-md font-medium text-foreground mb-4">Status History</h4>
        <div className="space-y-3">
          {statusHistory?.map((record, index) => {
            const statusInfo = statusOptions?.find(opt => opt?.value === record?.status);
            const previousStatusInfo = statusOptions?.find(opt => opt?.value === record?.previousStatus);
            
            return (
              <div key={record?.id} className="flex items-start space-x-4 p-3 border border-border rounded-lg">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    record?.isCurrent ? statusInfo?.color || 'bg-muted' : 'bg-muted'
                  }`}>
                    <Icon 
                      name={statusInfo?.icon || 'Circle'} 
                      size={14} 
                      color={record?.isCurrent ? 'white' : 'currentColor'}
                      className={!record?.isCurrent ? 'text-muted-foreground' : ''}
                    />
                  </div>
                  {index < statusHistory?.length - 1 && (
                    <div className="w-0.5 h-8 bg-border mt-2"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">{statusInfo?.label}</span>
                      {record?.previousStatus && (
                        <>
                          <Icon name="ArrowRight" size={12} className="text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">from {previousStatusInfo?.label}</span>
                        </>
                      )}
                      {record?.isCurrent && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success text-success-foreground">
                          Current
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">{formatDate(record?.changedDate)}</span>
                  </div>
                  
                  <div className="text-sm text-muted-foreground mb-1">
                    Changed by {record?.changedBy} • Reason: {reasonOptions?.find(r => r?.value === record?.reason)?.label || record?.reason}
                  </div>
                  
                  {record?.notes && (
                    <p className="text-sm text-foreground bg-muted/50 rounded p-2 mt-2">{record?.notes}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateControls;